import React, { useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
} from 'recharts';

import SafeChart from './common/SafeChart';

/**
 * NetIncomeTab – tabular & visual breakdown of income minus expenses.
 *
 * Props
 *  ─ netIncomeData: NetIncRow[]  [{ month, income, expenses, netIncome }]
 *  ─ chartsReady:   boolean      – passed straight to <SafeChart>
 *  ─ onUpdateIncomeData: function – callback to update income data
 *  ─ onUpdateExpenseData: function – callback to update expense data
 *  ─ addNextMonth: function – callback to add new month
 */
const NetIncomeTab = ({ 
  netIncomeData, 
  chartsReady, 
  onUpdateIncomeData,
  onUpdateExpenseData,
  addNextMonth
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Format number as currency for display
  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '' : `$${num.toLocaleString()}`;
  };

  // Parse currency string to number
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  };

  // Handle cell editing
  const handleCellClick = (rowIndex, field) => {
    if (field === 'month' || field === 'netIncome') return; // Don't allow editing month or calculated field
    
    setEditingCell({ rowIndex, field });
    const currentValue = netIncomeData[rowIndex][field];
    setEditValue(currentValue.toString());
  };

  const handleCellChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleCellBlur = () => {
    if (editingCell) {
      const { rowIndex, field } = editingCell;
      const numericValue = parseCurrency(editValue);
      
      // Update the appropriate data through parent callbacks
      if (field === 'income') {
        onUpdateIncomeData?.(rowIndex, numericValue);
      } else if (field === 'expenses') {
        onUpdateExpenseData?.(rowIndex, numericValue);
      }
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleCellKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    }
    if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const renderEditableCell = (value, rowIndex, field) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;
    
    if (isEditing) {
      return (
        <input
          type="text"
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyDown={handleCellKeyPress}
          className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
        />
      );
    }

    return (
      <span
        onClick={() => handleCellClick(rowIndex, field)}
        className="cursor-pointer hover:bg-white/[0.05] px-2 py-1 rounded transition-colors"
      >
        {formatCurrency(value)}
      </span>
    );
  };

  return (
    <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Net Income Analysis</h3>
        <button
          onClick={addNextMonth}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          + Add Month
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mb-6">
        <table className="min-w-full table-auto">
          <thead>
            <tr className="bg-white/[0.03]">
              {['Month', 'Total Income', 'Total Expenses', 'Net Income'].map((h) => (
                <th key={h} className="px-4 py-2 text-left first:text-left last:text-right text-right">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {netIncomeData.map((row, index) => (
              <tr key={`${row.month}-${index}`} className="border-b hover:bg-white/[0.03]">
                <td className="px-4 py-2 font-medium">{row.month}</td>
                <td className="px-4 py-2 text-right text-green-600">
                  {renderEditableCell(row.income, index, 'income')}
                </td>
                <td className="px-4 py-2 text-right text-red-600">
                  {renderEditableCell(row.expenses, index, 'expenses')}
                </td>
                <td
                  className={`px-4 py-2 text-right font-semibold ${
                    row.netIncome >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatCurrency(row.netIncome)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <h4 className="font-semibold text-green-800 mb-2">Total Income</h4>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(netIncomeData.reduce((sum, row) => sum + row.income, 0))}
          </p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="font-semibold text-red-800 mb-2">Total Expenses</h4>
          <p className="text-2xl font-bold text-red-600">
            {formatCurrency(netIncomeData.reduce((sum, row) => sum + row.expenses, 0))}
          </p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Net Total</h4>
          <p className={`text-2xl font-bold ${
            netIncomeData.reduce((sum, row) => sum + row.netIncome, 0) >= 0 
              ? 'text-green-600' 
              : 'text-red-600'
          }`}>
            {formatCurrency(netIncomeData.reduce((sum, row) => sum + row.netIncome, 0))}
          </p>
        </div>
      </div>

      {/* Bar Chart */}
      <SafeChart chartsReady={chartsReady}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={netIncomeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
            <Legend />
            <Bar dataKey="income" fill="#10b981" name="Income" />
            <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
            <Bar dataKey="netIncome" fill="#3b82f6" name="Net Income" />
          </BarChart>
        </ResponsiveContainer>
      </SafeChart>
    </div>
  );
};

export default NetIncomeTab;